/**
 * Persisted client state in localStorage. Same surface area as the desktop
 * client's ClientConfig minus everything that doesn't apply to a browser
 * (mic device, master gain - browser picks defaults).
 */

const KEY_REFRESH = 'aav.refreshToken'
const KEY_REGION = 'aav.region'
const KEY_SERVER = 'aav.serverId'
const KEY_LINKED_AGID = 'aav.linkedAgid'
const KEY_MASTER_GAIN = 'aav.masterGain'
const KEY_USER_GAINS = 'aav.userGains'
const KEY_MUTED_AGIDS = 'aav.mutedAgids'
const KEY_VOICE_MODE = 'aav.voiceMode'
const KEY_NOISE_GATE_THRESHOLD = 'aav.noiseGateThreshold'
const KEY_PTT_KEY = 'aav.pttKey'
const KEY_NOISE_SUPPRESSION = 'aav.noiseSuppression'
const KEY_MIC_DEVICE_ID = 'aav.micDeviceId'
const KEY_SPEAKER_DEVICE_ID = 'aav.speakerDeviceId'
const KEY_TTS_ENABLED = 'aav.ttsEnabled'
const KEY_TTS_HOTKEY = 'aav.ttsHotkey'

export type Region = 'eu' | 'na'

export function getRefreshToken(): string | null {
  const v = localStorage.getItem(KEY_REFRESH)
  return v !== null && v.length > 0 ? v : null
}

export function setRefreshToken(token: string | null): void {
  if (token === null || token.length === 0) {
    localStorage.removeItem(KEY_REFRESH)
  } else {
    localStorage.setItem(KEY_REFRESH, token)
  }
}

export function getRegion(): Region {
  const v = localStorage.getItem(KEY_REGION)
  return v === 'na' ? 'na' : 'eu'
}

export function setRegion(region: Region): void {
  localStorage.setItem(KEY_REGION, region)
}

export function getServerId(): string | null {
  return localStorage.getItem(KEY_SERVER)
}

export function setServerId(serverId: string | null): void {
  if (serverId === null) localStorage.removeItem(KEY_SERVER)
  else localStorage.setItem(KEY_SERVER, serverId)
}

export function getLinkedAgid(): string | null {
  return localStorage.getItem(KEY_LINKED_AGID)
}

export function setLinkedAgid(agid: string | null): void {
  if (agid === null) localStorage.removeItem(KEY_LINKED_AGID)
  else localStorage.setItem(KEY_LINKED_AGID, agid)
}

export function unlink(): void {
  setRefreshToken(null)
  setLinkedAgid(null)
}

/** Master output gain, persisted. Default 2.0 (+6 dB) to match other clients. */
export const DEFAULT_MASTER_GAIN = 2.0
export const MAX_MASTER_GAIN = 4.0
export const MAX_USER_GAIN = 4.0

export function getMasterGain(): number {
  const raw = localStorage.getItem(KEY_MASTER_GAIN)
  if (raw === null) return DEFAULT_MASTER_GAIN
  const n = Number.parseFloat(raw)
  if (!Number.isFinite(n)) return DEFAULT_MASTER_GAIN
  return Math.max(0, Math.min(MAX_MASTER_GAIN, n))
}

export function setMasterGain(value: number): void {
  const clamped = Math.max(0, Math.min(MAX_MASTER_GAIN, value))
  localStorage.setItem(KEY_MASTER_GAIN, String(clamped))
}

export function getUserGains(): Record<string, number> {
  const raw = localStorage.getItem(KEY_USER_GAINS)
  if (raw === null) return {}
  try {
    const parsed = JSON.parse(raw) as unknown
    if (typeof parsed !== 'object' || parsed === null) return {}
    const out: Record<string, number> = {}
    for (const [k, v] of Object.entries(parsed)) {
      if (typeof v === 'number' && Number.isFinite(v)) {
        out[k] = Math.max(0, Math.min(MAX_USER_GAIN, v))
      }
    }
    return out
  } catch {
    return {}
  }
}

export function setUserGain(agid: string, gain: number): void {
  const all = getUserGains()
  if (Math.abs(gain - 1.0) < 0.0001) {
    delete all[agid]
  } else {
    all[agid] = Math.max(0, Math.min(MAX_USER_GAIN, gain))
  }
  localStorage.setItem(KEY_USER_GAINS, JSON.stringify(all))
}

export function getMutedAgids(): ReadonlyArray<string> {
  const raw = localStorage.getItem(KEY_MUTED_AGIDS)
  if (raw === null) return []
  try {
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return []
    return parsed.filter((x): x is string => typeof x === 'string' && x.length > 0)
  } catch {
    return []
  }
}

export function setMutedAgids(agids: ReadonlyArray<string>): void {
  localStorage.setItem(KEY_MUTED_AGIDS, JSON.stringify(agids))
}

export type VoiceMode = 'ptt' | 'openMic'

/** Voice transmission mode. PTT (default) or open mic with VAD. */
export function getVoiceMode(): VoiceMode {
  const raw = localStorage.getItem(KEY_VOICE_MODE)
  return raw === 'openMic' ? 'openMic' : 'ptt'
}

export function setVoiceMode(mode: VoiceMode): void {
  localStorage.setItem(KEY_VOICE_MODE, mode)
}

/**
 * Peak amplitude (0..1) below which a frame is treated as silence in
 * open-mic mode. 0.012 ≈ -38 dBFS — matches the desktop client default
 * so a user switching devices gets the same gate behaviour out of the box.
 */
export const DEFAULT_NOISE_GATE_THRESHOLD = 0.012

export function getNoiseGateThreshold(): number {
  const raw = localStorage.getItem(KEY_NOISE_GATE_THRESHOLD)
  if (raw === null) return DEFAULT_NOISE_GATE_THRESHOLD
  const n = Number.parseFloat(raw)
  if (!Number.isFinite(n)) return DEFAULT_NOISE_GATE_THRESHOLD
  return Math.max(0, Math.min(1, n))
}

export function setNoiseGateThreshold(value: number): void {
  const clamped = Math.max(0, Math.min(1, value))
  localStorage.setItem(KEY_NOISE_GATE_THRESHOLD, String(clamped))
}

/**
 * Single-character PTT hotkey (A-Z or 0-9). null means no keyboard binding;
 * the on-screen hold button is always available regardless. We deliberately
 * scope this to single characters to mirror the desktop / mobile clients,
 * which use the same one-key convention.
 */
function sanitizeHotkey(value: string | null): string | null {
  if (value === null) return null
  const trimmed = value.trim().toUpperCase()
  if (trimmed.length !== 1) return null
  if (!/^[A-Z0-9]$/.test(trimmed)) return null
  return trimmed
}

export function getPttKey(): string | null {
  return sanitizeHotkey(localStorage.getItem(KEY_PTT_KEY))
}

export function setPttKey(value: string | null): void {
  const safe = sanitizeHotkey(value)
  if (safe === null) localStorage.removeItem(KEY_PTT_KEY)
  else localStorage.setItem(KEY_PTT_KEY, safe)
}

/**
 * Browser-native noise suppression + AEC + AGC, passed through to
 * `getUserMedia` constraints. Default on (matches what every modern client
 * expects). Persisted so a user who turned it off stays off after a reload.
 */
export const DEFAULT_NOISE_SUPPRESSION = true

export function getNoiseSuppression(): boolean {
  const raw = localStorage.getItem(KEY_NOISE_SUPPRESSION)
  if (raw === null) return DEFAULT_NOISE_SUPPRESSION
  return raw === '1'
}

export function setNoiseSuppression(enabled: boolean): void {
  localStorage.setItem(KEY_NOISE_SUPPRESSION, enabled ? '1' : '0')
}

/**
 * Persisted audio device IDs. Empty string / null means "browser default".
 * The actual device list is fetched at runtime via enumerateDevices; these
 * are only the user's last-chosen IDs.
 */
export function getMicDeviceId(): string | null {
  const v = localStorage.getItem(KEY_MIC_DEVICE_ID)
  return v !== null && v.length > 0 ? v : null
}

export function setMicDeviceId(deviceId: string | null): void {
  if (deviceId === null || deviceId.length === 0) localStorage.removeItem(KEY_MIC_DEVICE_ID)
  else localStorage.setItem(KEY_MIC_DEVICE_ID, deviceId)
}

export function getSpeakerDeviceId(): string | null {
  const v = localStorage.getItem(KEY_SPEAKER_DEVICE_ID)
  return v !== null && v.length > 0 ? v : null
}

export function setSpeakerDeviceId(deviceId: string | null): void {
  if (deviceId === null || deviceId.length === 0) localStorage.removeItem(KEY_SPEAKER_DEVICE_ID)
  else localStorage.setItem(KEY_SPEAKER_DEVICE_ID, deviceId)
}

/** Text-to-speech: master toggle + summon hotkey. */
export function getTtsEnabled(): boolean {
  return localStorage.getItem(KEY_TTS_ENABLED) === '1'
}

export function setTtsEnabled(enabled: boolean): void {
  localStorage.setItem(KEY_TTS_ENABLED, enabled ? '1' : '0')
}

export function getTtsHotkey(): string | null {
  return sanitizeHotkey(localStorage.getItem(KEY_TTS_HOTKEY))
}

export function setTtsHotkey(value: string | null): void {
  const safe = sanitizeHotkey(value)
  if (safe === null) localStorage.removeItem(KEY_TTS_HOTKEY)
  else localStorage.setItem(KEY_TTS_HOTKEY, safe)
}

/**
 * The bridge URL the client should target. In production the web client is
 * served from the bridge itself (same origin), so all `/api/*` calls are
 * relative. In dev under Vite's proxy, also relative. So we don't need a
 * region-keyed base URL at all on the web client - the proxy or the bridge
 * itself picks the right backend.
 *
 * Exception: WebSocket `wss://` URLs need an absolute origin. We derive it
 * from window.location.
 */
export function baseHttpOrigin(): string {
  return window.location.origin
}

export function baseWsOrigin(): string {
  return window.location.origin.replace(/^http/, 'ws')
}
